/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { SalesLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';

/**
 * Represents a single kit component line shown read-only beneath its parent kit line.
 */
export interface IKitChildProduct {
    salesLine: SalesLine;
    simpleProduct: SimpleProduct;
}

/**
 * Optional data describing a return product's eligibility, sourced from the
 * ReturnRequest CSU blob and the sales line.
 */
export interface IReturnOrderProductOptions {
    salesLineRecId?: number;
    eligibleReturnQty?: number;
    isEligible?: boolean;
    requestStatus?: string;
    isKitParent?: boolean;
    kitChildren?: IKitChildProduct[];
}

/**
 * Represents a product for order return.
 */
export class ReturnOrderProduct {
    public isSelected: boolean;

    public itemReturnQuantity: number;

    public readonly salesLine: SalesLine;

    public readonly simpleProduct: SimpleProduct;

    /** RecId of the underlying sales line, used when submitting the return request. */
    public readonly salesLineRecId: number;

    /** Quantity still eligible for return after any prior returns (from the CSU). */
    public readonly eligibleReturnQty: number;

    /** Whether the line can be returned (invoiced, shipped < 90 days, eligible qty > 0). */
    public readonly isEligible: boolean;

    /** Existing ecom return request status for the line, if any. */
    public readonly requestStatus?: string;

    /** Whether this row represents a kit parent line. */
    public readonly isKitParent: boolean;

    /** Read-only kit component lines displayed beneath a kit parent. */
    public readonly kitChildren: IKitChildProduct[];

    public constructor(salesLine: SalesLine, simpleProduct: SimpleProduct, options: IReturnOrderProductOptions = {}) {
        this.salesLine = salesLine;
        this.simpleProduct = simpleProduct;
        this.itemReturnQuantity = 0;
        this.isSelected = false;
        this.salesLineRecId = options.salesLineRecId ?? salesLine.RecordId ?? 0;
        this.eligibleReturnQty = options.eligibleReturnQty ?? 0;
        this.isEligible = options.isEligible ?? false;
        this.requestStatus = options.requestStatus;
        this.isKitParent = options.isKitParent ?? false;
        this.kitChildren = options.kitChildren ?? [];
    }
}
